/**
 * EL CAMPO DEL PESO («Todo lo validado antes del 1 de septiembre», bloque 4: «El peso. Un
 * solo registro, tres puertas»).
 *
 *     «Abierto todo el año. Es el único sitio donde el peso se escribe.»
 *
 * Antes se escribía DENTRO del cierre del día, al final de once preguntas. Con eso el que
 * no cerraba el día no tenía dónde apuntarlo -- y son la mayoría --, y el que lo apuntaba
 * dejaba el dato enterrado en un formulario que no vuelve a abrir. Ahora vive aquí, en
 * Evolución, que es de donde sale la curva, y las otras dos puertas del bloque 4 traen
 * hasta aquí: la fila «Hoy toca pesarte» de Inicio los días de pesada y el paso 1 del
 * reporte quincenal.
 *
 * LA FECHA DEL PESAJE SIGUE VIVA (punto 34 del doc 24-08). No sale en la maqueta porque la
 * maqueta enseña el campo en reposo, vacío, y en reposo tampoco salía en el cierre: aparece
 * al escribir un peso, y solo si hay más de un día al que fecharlo. Sin ella, el que se
 * pesa por la mañana y lo anota de noche rompe la pareja de días seguidos de la que sale la
 * media semanal.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { revisarPeso, PESO_MIN, PESO_MAX, kg } from '../lib/pesoValido';
import { useConfirm } from './ui/confirm';
import { mensajeDeError } from '../lib/mensajeDeError';
import PesoDeReferencia, { PesoSemanalElegido } from './PesoDeReferencia';
import { ahoraDeLaApp } from '../lib/horaEspana';

// El día de HOY es el del navegador, no el del servidor: el día vivido es el suyo.
const hoyDelCliente = () => ahoraDeLaApp().toLocaleDateString('en-CA');

// Los días a los que puede fechar un pesaje: hoy y los `diasAtras` anteriores. El número lo
// dice el servidor (`peso_dias_atras`), que es el que luego lo acepta o no.
export const diasParaFechar = (hoyIso, diasAtras = 14) => {
    const dias = [];
    for (let i = 0; i <= diasAtras; i++) {
        const d = new Date(`${hoyIso}T12:00:00`);
        d.setDate(d.getDate() - i);
        const etiqueta = i === 0 ? 'Hoy' : i === 1 ? 'Ayer'
            : d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        dias.push({
            iso: d.toLocaleDateString('en-CA'),
            etiqueta: etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1),
        });
    }
    return dias;
};

const cuando = (iso) => {
    if (!iso) return '';
    const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};

/**
 * @param ultimo   {valor, fecha} del último pesaje conocido, para la línea de abajo y para
 *                 medir el salto que canta.
 * @param abrir    llega con el foco puesto: es el que viene de «Hoy toca pesarte», que
 *                 espera escribir sin buscar nada.
 */
/**
 * @param onSemana  (estado) => …, con el estado de su peso de referencia semanal
 *                  (`GET /clients/me/peso-semanal`) cada vez que cambia: al guardar un peso y
 *                  al elegir el de la semana. Lo usa la puerta del quincenal para saber
 *                  cuándo puede abrir el reporte.
 * @param compacto  solo la casilla y su «Último registro»: sin la fecha, sin los textos de
 *                  debajo. Es la pantalla «Tu peso de hoy» del quincenal (26-10).
 * @param soloHoy   el del check-in diario (artefacto de Enrique, 26-10): el peso es el de hoy,
 *                  sin «¿De qué día es este peso?» (Enrique, 27-10: «le pedía elegir un
 *                  peso»), sin el rótulo «Tu peso», con el de hoy ya escrito en la casilla y
 *                  «Último registro» en cursiva encima del texto.
 */
/**
 * @param botonAbajo  el «Tu peso» del mensual (28-10): el mismo campo del check-in, con el botón
 *                    naranja de texto negro «REGISTRAR PESO» a todo lo ancho, debajo, en vez de
 *                    «Guardar» al lado.
 */
const CampoDePeso = ({ api, ultimo = null, diasAtras = 14, abrir = false, onGuardado,
                       pesoDelDia = null, pesoSemanal = null, detalleSemanal = null,
                       conLosDosNumeros = true, onSemana, compacto = false, soloHoy = false,
                       botonAbajo = false }) => {
    // En el check-in, el de hoy ya apuntado se queda escrito en la casilla (artefacto, 26-10).
    const deHoy = ultimo?.valor != null && String(ultimo.fecha || '').slice(0, 10) === hoyDelCliente();
    const [valor, setValor] = useState(soloHoy && deHoy ? String(ultimo.valor) : '');
    const [fecha, setFecha] = useState(hoyDelCliente);
    const [guardando, setGuardando] = useState(false);
    // EL PESO DE REFERENCIA SEMANAL (artefacto del quincenal, 26-10: «todas las semanas»). Tras
    // guardar se pregunta al servidor si toca ofrecerlo; `elegido` es el que acaba de elegir,
    // para enseñárselo en vez del ofrecimiento.
    const [semana, setSemana] = useState(null);
    const [elegido, setElegido] = useState(null);
    // «Cambiar» el que ya eligió esta semana (Francisco, 27-10): vuelve a la lista.
    const [cambiando, setCambiando] = useState(false);
    const { confirm } = useConfirm();
    const campo = React.useRef(null);

    useEffect(() => { if (abrir) campo.current?.focus(); }, [abrir]);
    // El de hoy puede llegar después de pintar la casilla (el check-in lo pide al abrir).
    useEffect(() => {
        if (soloHoy && deHoy) setValor(String(ultimo.valor));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [soloHoy, deHoy, ultimo?.valor]);

    // ¿HOY TOCA PESARSE? (punto 30 de Jesús: «Inicio le dice "hoy toca pesarte" y el campo
    // le dice "registrarlo es opcional"»). Las dos frases son verdad por separado y juntas
    // se contradicen delante del cliente.
    //
    // Se pregunta al MISMO sitio que pregunta Inicio (`/checkins/estado`), no a una regla
    // copiada aquí: dos reglas paralelas es como nacen estas contradicciones. Si la llamada
    // falla se queda en «no toca», que es la frase de siempre y la que no promete nada.
    const [tocaPesarse, setTocaPesarse] = useState(false);
    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const r = await api.get(
                    `/checkins/estado?fecha=${hoyDelCliente()}&hora_cliente=${ahoraDeLaApp().getHours()}`);
                if (vivo) setTocaPesarse(!!r?.data?.toca_pesarse);
            } catch (e) {
                // Sin respuesta, la frase de siempre. No se le dice nada al cliente: esto no
                // es un fallo suyo ni le impide escribir su peso.
            }
        })();
        return () => { vivo = false; };
    }, [api]);

    const dias = diasParaFechar(hoyDelCliente(), diasAtras);

    const guardar = async () => {
        const chequeo = revisarPeso(valor, ultimo?.valor);
        if (!chequeo.ok) return toast.error(chequeo.error);
        if (chequeo.confirmar) {
            const sigue = await confirm({
                title: 'Confírmame el peso',
                description: chequeo.confirmar,
                confirmLabel: 'Sí, es correcto', cancelLabel: 'Lo corrijo',
            });
            if (!sigue) return;
        }
        setGuardando(true);
        // En el check-in, el día de AHORA, al guardar: abierto a las 23:50 y guardado a las
        // 00:05 es el peso del día nuevo (revisor, 27-10). Ahí no hay desplegable que lo corrija.
        const fechaDelPeso = soloHoy ? hoyDelCliente() : fecha;
        try {
            const r = await api.post('/clients/me/peso', { valor: chequeo.peso, fecha: fechaDelPeso });
            // LO QUE DIGA EL SERVIDOR, NO LO QUE HEMOS MANDADO (10-09).
            //
            // Desde la regla de Jesús del 8-09 -- «una pesada por día, y si se pesa dos veces
            // el mismo miércoles cuenta la primera» -- hay un caso en que el peso NO se
            // guarda. Y aquí se decía «Peso guardado» igual: el cliente escribía una
            // corrección, leía que se había guardado y el número de la tarjeta no se movía.
            // Eso no es un detalle de texto; es la app diciéndole que tiene su dato cuando
            // no lo tiene.
            const yaHabia = r?.data?.ya_habia;
            if (r?.data?.guardado === false && yaHabia != null) {
                // SIN TILDES DE LAS QUE SE FUERON (Jesús, 11-09). «Sólo» y «ése» ya no la
                // llevan, y aquí salían las dos a dos líneas de un «Solo necesitamos dos
                // pesadas» bien escrito, en la misma pantalla.
                toast.info(`Ya habías apuntado ${kg(yaHabia)} kg ese día y se queda ese. `
                    + 'Solo contamos la primera pesada de cada día.', { siempre: true });
            } else {
                toast.success('Peso guardado');
            }
            setValor(soloHoy ? String(yaHabia ?? chequeo.peso) : '');
            setFecha(hoyDelCliente());
            // Con el peso que quedó (el que ya había, si no se guardó): la puerta del
            // quincenal lo usa para su «Hoy apuntaste»; los demás vuelven a pedir el suyo.
            onGuardado?.({ valor: yaHabia ?? chequeo.peso, fecha: fechaDelPeso });
            try {
                const s = await api.get('/clients/me/peso-semanal', { params: { hoy: hoyDelCliente() } });
                setSemana(s.data);
                // Si esta semana ya eligió uno, se le enseña con «Cambiar»; si no, se le ofrece.
                setElegido(s.data?.ofrecer ? null : s.data?.elegido || null);
                setCambiando(false);
                onSemana?.(s.data);
            } catch (e) {
                // Sin respuesta no se le ofrece nada: el peso ya está guardado, que es lo suyo.
                console.error('No se pudo leer el peso de referencia semanal', e);
            }
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo guardar el peso'));
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div data-testid="campo-de-peso">
            {/* SIN LA ETIQUETA «SIEMPRE ABIERTO» (Jesús, 8-09: «la etiqueta sobra»). Decía
                en verde que el campo está disponible, que es justo lo que ya se ve: el
                campo está ahí y se puede escribir en él. */}
            {!compacto && !soloHoy && (
                <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Tu peso
                    </p>
                </div>
            )}
            <div className="flex gap-2">
                <div className="flex-1 bg-muted rounded-xl px-3 py-2.5 flex items-center justify-between">
                    <input ref={campo} type="number" step="0.1" min={PESO_MIN} max={PESO_MAX}
                        value={valor} onChange={e => setValor(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') guardar(); }}
                        data-testid="peso-valor" placeholder="0,0" aria-label="Tu peso en kilos"
                        className="w-full bg-transparent border-0 p-0 text-sm text-foreground
                            placeholder:text-muted-foreground focus:outline-none" />
                    <span className="text-xs text-muted-foreground font-data pl-2">kg</span>
                </div>
                {!botonAbajo && (
                    <button onClick={guardar} disabled={guardando || !valor}
                        data-testid="guardar-peso"
                        className={`btn-brand rounded-xl font-bold disabled:opacity-60 ${soloHoy
                            ? 'px-4 text-[11px] uppercase tracking-wider' : 'px-5 text-sm'}`}>
                        {guardando ? 'Guardando…' : 'Guardar'}
                    </button>
                )}
            </div>

            {(semana?.ofrecer || cambiando) && (
                <div className="mt-3">
                    <PesoDeReferencia api={api} estado={semana} cambiando={cambiando}
                        onCancelar={() => { setCambiando(false); onSemana?.(semana); }}
                        onElegido={(nuevo) => {
                            setSemana(nuevo); setElegido(nuevo.elegido); setCambiando(false); onSemana?.(nuevo);
                        }} />
                </div>
            )}
            {elegido && !cambiando && (
                <div className="mt-3">
                    {/* Mientras cambia, quien escucha lo ve como un ofrecimiento abierto: la
                        puerta del quincenal esconde «Abrir el reporte» hasta que confirme. */}
                    {/* «Cambiar» solo con más de un peso en la semana: con uno no hay a cuál. */}
                    <PesoSemanalElegido elegido={elegido}
                        onCambiar={(semana?.pesos?.length || 0) > 1
                            ? () => { setCambiando(true); onSemana?.({ ...semana, ofrecer: true }); }
                            : null} />
                </div>
            )}

            {!compacto && !soloHoy && valor && dias.length > 1 && (
                <div className="mt-2">
                    <label className="text-[11px] text-muted-foreground block mb-1" htmlFor="peso-fecha">
                        ¿De qué día es este peso?
                    </label>
                    <select id="peso-fecha" value={fecha} onChange={e => setFecha(e.target.value)}
                        data-testid="peso-fecha"
                        className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground">
                        {dias.map(d => <option key={d.iso} value={d.iso}>{d.etiqueta}</option>)}
                    </select>
                </div>
            )}

            {/* EL TEXTO DE DEBAJO, EL SUYO (Jesús, 8-09). Las dos frases que había se van
                las dos, y él dice por qué:

                  «Registrarlo es opcional, sólo para ti»  — no era verdad
                  «Te lo pediremos sólo para los reportes» — deja de serlo

                Ya no es opcional ni es sólo para el reporte: el peso pasa a ser un dato de
                la semana, y de dos pesadas sale su peso semanal. El texto lo dice y de paso
                dice que le avisaremos, que es la parte que la app ya hace (le pide pesarse
                el miércoles y el jueves hasta las 12:00).

                La frase de los días que toca se queda: es una instrucción para HOY y no
                compite con esto, que es la regla de siempre. */}
            {/* «HOY TOCA» SE SUMA, NO SUSTITUYE (10-09).
                Estaba con un `?:` y los miércoles y jueves antes de las 12 -- justo los días
                de su ventana -- el texto de Jesús NO SE VEÍA. El `?:` se puso el 5-09 para
                su punto 30: «Inicio le dice "hoy toca pesarte" y el campo le dice
                "registrarlo es opcional"», dos frases que juntas se contradicen. Pero esa
                segunda frase la quita él mismo en el bloque A del 8-09, así que la
                contradicción ya no existe y con ella se va el motivo de esconder nada.
                Manda lo más reciente: su texto va siempre, y la instrucción del día encima. */}
            {soloHoy && ultimo?.valor != null && !deHoy && (
                <p className="text-[11px] text-muted-foreground italic mt-2" data-testid="peso-ultimo">
                    Último registro: {kg(ultimo.valor)} kg · {cuando(ultimo.fecha)}
                </p>
            )}
            {botonAbajo && (
                <button type="button" onClick={guardar} disabled={guardando || !valor}
                    data-testid="registrar-peso"
                    className="mt-4 w-full rounded-xl py-3 text-sm font-bold uppercase tracking-wider text-black disabled:opacity-50"
                    style={{ backgroundColor: '#FF671F' }}>
                    {guardando ? 'Guardando…' : 'Registrar peso'}
                </button>
            )}
            {!compacto && tocaPesarse && (
                <p className="text-[11px] text-foreground font-medium mt-2 leading-snug"
                   data-testid="peso-hoy-toca">
                    Hoy toca: en ayunas y después de ir al baño.
                </p>
            )}
            {/* EL TEXTO DEL CHECK-IN DEL 26-10 (artefacto de Enrique): «un día por semana»,
                que es lo que pide ya el quincenal (un peso de referencia por semana). Manda
                sobre el de abajo, que pedía dos.
                Y SU TEXTO DE ANTES (Jesús, vídeo del 20-10: «esto es lo que tienes que poner
                debajo del peso»). Lo pidió para el check-in diario, y es la misma regla que
                la frase del 8-09 dicha otra vez: manda lo más reciente y va en el único
                campo del peso, que es el que monta también el cierre. Es un texto que pide
                él: se queda aunque el 20-10 se quitaran los de ayuda. */}
            {!compacto && <p className="text-[11px] text-muted-foreground mt-2 leading-snug"
                data-testid="peso-cuando">
                Puedes pesarte todos los días, pero no hace falta. Con que lo hagas <strong className="text-foreground/80">un día por semana</strong> es suficiente. Ideal: <strong className="text-foreground/80">miércoles o jueves</strong>.
            </p>}
            {/* LOS DOS NÚMEROS (Jesús, 8-09): «el del día y el de la semana, con su último
                registro cada uno».
                  · el del día NO lleva fecha: es el de hoy. Si no se ha pesado, «Sin apuntar».
                  · el semanal SÍ, y con los días del mes, «porque puede ser de la semana
                    pasada y hay que saber de cuándo es ese número».
                  · y la cursiva de debajo SE QUEDA SIEMPRE, tenga datos o no: «cuando está
                    vacío es cuando más falta hace».
                Sustituye a la línea «Tu último peso: 80 kg · 9 de septiembre», que decía el
                último fuera de cuando fuera y no distinguía el día de la semana. */}
            {/* Y SOLO DONDE SE SABEN (13-09). Los dos numeros salen de quien monta el
                campo, y donde este es una PUERTA para escribir -- el hito de «Mi semana»,
                el cierre del dia -- nadie se los pasa: se quedaban los dos en «Sin
                apuntar» tuviera el cliente pesadas o no, que es la app mintiendo en un
                sitio donde ademas no se venia a leer nada. Su casa es Evolucion, que es
                quien los tiene. */}
            {/* Y DONDE NO VAN LOS DOS, SU ULTIMO REGISTRO (Francisco, 13-09). Su maqueta
                del cierre lo pone asi: «Ultimo registro: 96 kg · 22 de julio». Es el dato
                que tiene quien monta esta puerta, y sin el la casilla se queda muda: no
                sabe si lo de hoy sube o baja. */}
            {/* EL PESO DE HOY SE QUEDA A LA VISTA (Francisco, 23-10: «que el check-in
                recuerde el peso ingresado»). Al guardar se vaciaba la casilla y debajo salía
                «Último registro», que no decía si ese era el de hoy. Si ya lo apuntó hoy,
                se dice así; la casilla sigue libre para corregirlo. */}
            {!conLosDosNumeros && !soloHoy && deHoy && (
                <p className="text-[12px] text-emerald-500 font-semibold mt-2" data-testid="peso-de-hoy">
                    Hoy apuntaste {kg(ultimo.valor)} kg
                </p>
            )}
            {!conLosDosNumeros && !soloHoy && ultimo?.valor != null && !deHoy && (
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="peso-ultimo">
                    Último registro: {kg(ultimo.valor)} kg · {cuando(ultimo.fecha)}
                </p>
            )}
            {conLosDosNumeros && (
            <div className="mt-3 pt-3 border-t border-border space-y-1" data-testid="peso-dos-numeros">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[11px] text-muted-foreground">Tu peso del día</span>
                    <span className="text-sm font-data font-bold text-foreground" data-testid="peso-del-dia">
                        {pesoDelDia != null ? `${kg(pesoDelDia)} kg` : 'Sin apuntar'}
                    </span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[11px] text-muted-foreground">Tu peso semanal</span>
                    <span className="text-sm font-data font-bold text-foreground" data-testid="peso-semanal">
                        {pesoSemanal != null ? `${kg(pesoSemanal)} kg` : 'Sin apuntar'}
                    </span>
                </div>
                {detalleSemanal && (
                    <p className="text-[11px] text-muted-foreground/80 text-right leading-tight"
                       data-testid="peso-semanal-detalle">
                        {detalleSemanal}
                    </p>
                )}
                <p className="text-[11px] text-muted-foreground/70 italic leading-snug pt-1"
                   data-testid="peso-ventana">
                    Idealmente entre miércoles y viernes, para evitar rebotes del fin de semana.
                </p>
            </div>
            )}
        </div>
    );
};

export default CampoDePeso;
